/**
 * Sport Highlights block — front-end behaviour.
 *
 * /sport page V2 composite (CDN-1753 + CDN-1754). Two coordinated parts:
 *
 *   1. FILTER tab bar — one tab per carousel row. Clicking a tab smooth-scrolls
 *      to its row. An IntersectionObserver marks the in-view row's tab active
 *      and scrolls it into view. A scroll-position indicator (a scrollbar-style
 *      thumb) reflects the strip's horizontal scroll and hides when it fits.
 *
 *   2. CAROUSELS — each row's track pages by one viewport-width via prev/next
 *      buttons and disables them at the ends. A solid divider line sits in the
 *      header. Mobile uses native horizontal swipe + scroll-snap (the buttons
 *      are hidden via CSS), so JS only enhances; it never gates scrolling.
 *
 * Vanilla JS, no jQuery. Guarded to run only when `.sport-highlights` exists.
 * Respects prefers-reduced-motion (jumps instead of smooth-scrolls).
 */

(function () {
	'use strict';

	// Coalesce rapid scroll/resize callbacks to one run per animation frame, so
	// handlers that read layout (offsetWidth/scrollWidth) and then mutate styles
	// don't thrash layout during a continuous scroll or resize drag.
	function rafThrottle(fn) {
		var scheduled = false;
		var raf = window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
		return function () {
			if (scheduled) {
				return;
			}
			scheduled = true;
			raf(function () {
				scheduled = false;
				fn();
			});
		};
	}

	function init() {
		var blocks = document.querySelectorAll('.sport-highlights');
		if (!blocks.length) {
			return;
		}

		var prefersReducedMotion = window.matchMedia
			? window.matchMedia('(prefers-reduced-motion: reduce)').matches
			: false;

		blocks.forEach(function (block) {
			initFilter(block, prefersReducedMotion);
			initCarousels(block, prefersReducedMotion);
		});
	}

	/* ------------------------------------------------------------------
	 * Filter tab bar
	 * ---------------------------------------------------------------- */

	function initFilter(block, prefersReducedMotion) {
		var nav = block.querySelector('.sport-highlights__filter');
		if (!nav) {
			return;
		}

		var track = nav.querySelector('.sport-highlights__filter-track');
		var underline = nav.querySelector('.sport-highlights__filter-underline');
		var tabs = Array.prototype.slice.call(
			nav.querySelectorAll('.sport-highlights__tab')
		);
		if (!tabs.length) {
			return;
		}

		// Map anchor id -> tab element, and collect the target row sections.
		var tabByTarget = {};
		var rows = [];
		tabs.forEach(function (tab) {
			var targetId = tab.getAttribute('data-sh-target');
			if (!targetId) {
				return;
			}
			tabByTarget[targetId] = tab;
			var row = document.getElementById(targetId);
			if (row) {
				rows.push(row);
			}
		});

		var activeTab = null;

		function setActive(tab) {
			if (!tab || tab === activeTab) {
				return;
			}
			tabs.forEach(function (t) {
				t.classList.remove('sport-highlights__tab--active');
				t.removeAttribute('aria-current');
			});
			tab.classList.add('sport-highlights__tab--active');
			tab.setAttribute('aria-current', 'true');
			activeTab = tab;
			scrollTabIntoView(tab);
		}

		// Clear the active state entirely — used when scrolled above all rows
		// (the filter/featured area), where no row is in the tracking band and
		// no tab should look selected.
		function clearActive() {
			if (!activeTab) {
				return;
			}
			tabs.forEach(function (t) {
				t.classList.remove('sport-highlights__tab--active');
				t.removeAttribute('aria-current');
			});
			activeTab = null;
		}

		// Scroll-position indicator: the underline is a horizontal scrollbar
		// "thumb" for the tab strip. Its width is the visible fraction of the
		// strip and it slides as the strip scrolls. Hidden when the strip does
		// not overflow (nothing to scroll) — it signals "scroll for more".
		function updateScrollIndicator() {
			if (!underline || !track || !tabs.length) {
				return;
			}
			// Tabs' full layout extent (offsetLeft is unaffected by scroll).
			var lastTab = tabs[tabs.length - 1];
			var contentW = lastTab.offsetLeft + lastTab.offsetWidth;
			var clientW = track.clientWidth;

			// Scroller only shows when the strip overflows the viewport width;
			// when all tabs fit there is nothing to scroll, so hide it.
			if (contentW <= clientW + 1) {
				nav.classList.remove('is-scrollable');
				underline.classList.remove('is-visible');
				underline.style.width = '0px';
				return;
			}

			nav.classList.add('is-scrollable');

			// Overflow: a scrollbar-style thumb that slides with the strip.
			var thumbW = Math.max(24, (clientW / contentW) * clientW);
			var maxLeft = clientW - thumbW;
			var scrollMax = contentW - clientW;
			var left = scrollMax > 0 ? (track.scrollLeft / scrollMax) * maxLeft : 0;

			underline.style.width = Math.round(thumbW) + 'px';
			underline.style.transform = 'translateX(' + Math.round(left) + 'px)';
			underline.classList.add('is-visible');
		}

		function scrollTabIntoView(tab) {
			var tabLeft = tab.offsetLeft;
			var tabRight = tabLeft + tab.offsetWidth;
			var viewLeft = track.scrollLeft;
			var viewRight = viewLeft + track.clientWidth;

			if (tabLeft < viewLeft) {
				track.scrollLeft = tabLeft - 16;
			} else if (tabRight > viewRight) {
				track.scrollLeft = tabRight - track.clientWidth + 16;
			}
		}

		// Click / keyboard: smooth-scroll the row into view. Default anchor
		// jump is fine for keyboard, but we intercept to honour reduced motion
		// and to set the active state immediately.
		tabs.forEach(function (tab) {
			tab.addEventListener('click', function (event) {
				var targetId = tab.getAttribute('data-sh-target');
				var row = targetId ? document.getElementById(targetId) : null;
				if (!row) {
					return;
				}
				event.preventDefault();
				setActive(tab);
				row.scrollIntoView({
					behavior: prefersReducedMotion ? 'auto' : 'smooth',
					block: 'start',
				});
				// Keep the URL hash in sync without an extra jump.
				if (window.history && window.history.replaceState) {
					window.history.replaceState(null, '', '#' + targetId);
				}
			});
		});

		// Strip scroll + viewport resize update the scroll-position indicator
		// (thumb). Coalesced to one measure/mutate per frame to avoid thrash.
		var onIndicator = rafThrottle(updateScrollIndicator);
		track.addEventListener('scroll', onIndicator, { passive: true });
		window.addEventListener('resize', onIndicator);

		// IntersectionObserver: the row whose top is nearest the upper third of
		// the viewport becomes active. rootMargin biases towards the top so a
		// row activates as it scrolls up under the sticky header.
		if ('IntersectionObserver' in window && rows.length) {
			var visible = {};
			var observer = new IntersectionObserver(
				function (entries) {
					entries.forEach(function (entry) {
						visible[entry.target.id] = entry.isIntersecting
							? entry.intersectionRatio
							: 0;
					});

					// Pick the most-visible row currently intersecting.
					var bestId = null;
					var bestRatio = 0;
					Object.keys(visible).forEach(function (id) {
						if (visible[id] > bestRatio) {
							bestRatio = visible[id];
							bestId = id;
						}
					});

					if (bestId && tabByTarget[bestId]) {
						setActive(tabByTarget[bestId]);
					} else {
						// No row in the tracking band (scrolled above the first
						// row) — drop the highlight so no tab looks selected.
						clearActive();
					}
				},
				{
					rootMargin: '-20% 0px -60% 0px',
					threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
				}
			);

			rows.forEach(function (row) {
				observer.observe(row);
			});
		}

		// Don't seed an active tab on load — at the top of the page (filter +
		// featured group) no carousel row is in view yet, so highlighting the
		// first tab reads as a false "selected" state. The IntersectionObserver
		// activates the right tab once a row scrolls into view, and a click
		// activates immediately. Only seed the scroll-position indicator.
		updateScrollIndicator();
	}

	/* ------------------------------------------------------------------
	 * Carousels
	 * ---------------------------------------------------------------- */

	function initCarousels(block, prefersReducedMotion) {
		var carousels = block.querySelectorAll('[data-sh-carousel]');

		carousels.forEach(function (carousel) {
			var track = carousel.querySelector('.sport-highlights__carousel-track');
			if (!track) {
				return;
			}

			var row = carousel.closest('.sport-highlights__row');
			var header = row
				? row.querySelector('.sport-highlights__row-header')
				: null;
			var prevBtn = header
				? header.querySelector('.sport-highlights__nav--prev')
				: null;
			var nextBtn = header
				? header.querySelector('.sport-highlights__nav--next')
				: null;

			function maxScroll() {
				return Math.max(0, track.scrollWidth - track.clientWidth);
			}

			function update() {
				var max = maxScroll();
				var left = track.scrollLeft;

				// Disable prev/next at the ends (1px tolerance for sub-pixel).
				if (prevBtn) {
					prevBtn.disabled = left <= 1;
				}
				if (nextBtn) {
					nextBtn.disabled = left >= max - 1;
				}
			}

			function page(direction) {
				// Scroll by one visible page (the viewport width).
				var amount = track.clientWidth * direction;
				if (track.scrollBy) {
					track.scrollBy({
						left: amount,
						behavior: prefersReducedMotion ? 'auto' : 'smooth',
					});
				} else {
					track.scrollLeft += amount;
				}
			}

			if (prevBtn) {
				prevBtn.addEventListener('click', function () {
					page(-1);
				});
			}
			if (nextBtn) {
				nextBtn.addEventListener('click', function () {
					page(1);
				});
			}

			var onUpdate = rafThrottle(update);
			track.addEventListener('scroll', onUpdate, { passive: true });
			window.addEventListener('resize', onUpdate);

			// Initial paint (next frame so layout/scrollWidth is settled).
			if (window.requestAnimationFrame) {
				window.requestAnimationFrame(update);
			} else {
				update();
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
